/**
 * AdminDesignSystem — Página de referência do Design System
 * 4 abas: Identidade Visual, Componentes, Padrões, Estratégia
 */
import { useState } from 'preact/hooks';
import {
  TabBar, Button, Badge, Card, MetricCard, Input, Select, Textarea,
  Toggle, Modal, FormGroup, EmptyState, SectionHeader, ProgressBar,
  PageShell, PageLoading, TableSkeleton, CardSkeleton, ListSkeleton, SkeletonBar,
  Tooltip, Dropdown, SearchInput, Avatar, StatusBadge, Divider, ConfirmDialog,
  CopyButton, Pagination, FilterChips, Spinner,
  Table, Toast, useToast, Alert, Accordion,
} from '../components/ui';
import type { DropdownItem } from '../components/ui';
import { T } from '../styles/tokens';
import { componentRegistry, getCategories } from '../components/ui/registry';
import { useTheme } from '../hooks/useTheme';

// ============================================================
// HELPERS
// ============================================================

function CodeBlock({ code }: { code: string }) {
  return (
    <pre style={{
      background: T.bgOverlay, padding: 12, borderRadius: 8,
      fontSize: 12, fontFamily: "'SF Mono', monospace", color: T.textSecondary,
      overflow: 'auto', marginTop: 8,
    }}>{code}</pre>
  );
}

function ComponentSection({ title, children }: { title: string; children: any }) {
  return (
    <div style={{ marginBottom: 32, paddingBottom: 24, borderBottom: `1px solid ${T.border}` }}>
      <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 12 }}>{title}</h3>
      {children}
    </div>
  );
}

// ============================================================
// ABA 1 — IDENTIDADE VISUAL
// ============================================================

const COLOR_KEYS = Object.entries(T).filter(([_, v]) => typeof v === 'string' && (v.startsWith('#') || v.startsWith('rgba')));
const SPACING_KEYS = Object.entries(T).filter(([k]) => k.startsWith('space'));
const RADIUS_KEYS = Object.entries(T).filter(([k]) => k.startsWith('radius'));
const FONT_SIZE_KEYS = Object.entries(T).filter(([k]) => k.startsWith('text') && typeof (T as any)[k] === 'number');
const SHADOW_KEYS = Object.entries(T).filter(([k]) => k.startsWith('shadow'));
const TRANSITION_KEYS = Object.entries(T).filter(([k]) => k.startsWith('transition'));

function TabIdentidade() {
  return (
    <div>
      {/* Paleta de Cores */}
      <h2 style={{ fontSize: T.textXl, fontWeight: T.weightBold, color: T.text, marginBottom: T.space4 }}>Paleta de Cores</h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: T.space4, marginBottom: T.space8 }}>
        {COLOR_KEYS.map(([name, value]) => (
          <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 280 }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%', background: value as string,
              border: `1px solid ${T.borderHover}`, flexShrink: 0,
            }} />
            <div>
              <div style={{ fontSize: T.textBase, fontWeight: T.weightSemibold, color: T.text }}>T.{name}</div>
              <div style={{ fontSize: T.textXs, color: T.textMuted, fontFamily: T.fontMono }}>{value as string}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tipografia */}
      <h2 style={{ fontSize: T.textXl, fontWeight: T.weightBold, color: T.text, marginBottom: T.space4 }}>Tipografia</h2>
      <div style={{ marginBottom: T.space8, fontFamily: T.fontFamily }}>
        {FONT_SIZE_KEYS.map(([name, size]) => (
          <div key={name} style={{ fontSize: size as number, color: T.text, marginBottom: T.space2 }}>
            T.{name} — {size as number}px
          </div>
        ))}
        <div style={{ fontSize: T.textSm, fontFamily: T.fontMono, color: T.text, marginTop: T.space2 }}>Mono — {T.fontMono}</div>
      </div>

      {/* Espaçamentos */}
      <h2 style={{ fontSize: T.textXl, fontWeight: T.weightBold, color: T.text, marginBottom: T.space4 }}>Espacamentos</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: T.space2, marginBottom: T.space8 }}>
        {SPACING_KEYS.map(([name, val]) => (
          <div key={name} style={{ display: 'flex', alignItems: 'center', gap: T.space3 }}>
            <span style={{ fontSize: T.textSm, color: T.textSecondary, width: 80, textAlign: 'right', fontFamily: T.fontMono }}>T.{name}</span>
            <span style={{ fontSize: T.textSm, color: T.textMuted, width: 40 }}>{val as number}px</span>
            <div style={{ width: (val as number) * 4, height: T.space4, background: T.primary, borderRadius: T.radiusSm, opacity: 0.6 }} />
          </div>
        ))}
      </div>

      {/* Border Radius */}
      <h2 style={{ fontSize: T.textXl, fontWeight: T.weightBold, color: T.text, marginBottom: T.space4 }}>Border Radius</h2>
      <div style={{ display: 'flex', gap: T.space4, marginBottom: T.space8 }}>
        {RADIUS_KEYS.map(([name, val]) => (
          <div key={name} style={{ textAlign: 'center' }}>
            <div style={{
              width: 60, height: 60, borderRadius: val as number,
              background: T.bgCardHover, border: `1px solid ${T.borderHover}`,
            }} />
            <div style={{ fontSize: T.textXs, color: T.textSecondary, marginTop: T.space1 }}>T.{name}</div>
            <div style={{ fontSize: T.textXs, color: T.textMuted }}>{val as number}px</div>
          </div>
        ))}
      </div>

      {/* Sombras */}
      <h2 style={{ fontSize: T.textXl, fontWeight: T.weightBold, color: T.text, marginBottom: T.space4 }}>Sombras</h2>
      <div style={{ display: 'flex', gap: T.space4, marginBottom: T.space8 }}>
        {SHADOW_KEYS.map(([name, val]) => (
          <div key={name} style={{ textAlign: 'center' }}>
            <div style={{
              width: 80, height: 80, borderRadius: T.radiusMd,
              background: T.bgCardHover, boxShadow: val as string,
            }} />
            <div style={{ fontSize: T.textXs, color: T.textSecondary, marginTop: T.space2 }}>T.{name}</div>
          </div>
        ))}
      </div>

      {/* Transicoes */}
      <h2 style={{ fontSize: T.textXl, fontWeight: T.weightBold, color: T.text, marginBottom: T.space4 }}>Transicoes</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: T.space2, marginBottom: T.space8 }}>
        {TRANSITION_KEYS.map(([name, val]) => (
          <div key={name} style={{ fontSize: T.textBase, color: T.textSecondary }}>
            <span style={{ fontFamily: T.fontMono, color: T.text }}>T.{name}</span> → {val as string}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// ABA 2 — COMPONENTES (auto-discovery via registry)
// ============================================================

function TabComponentes() {
  const categories = getCategories(componentRegistry);

  return (
    <div>
      {categories.map(cat => (
        <div key={cat}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 16, marginTop: 32 }}>{cat}</h2>
          {componentRegistry.filter(c => c.category === cat).map(comp => (
            <ComponentSection key={comp.name} title={comp.name}>
              <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 8 }}>{comp.description}</p>
              <p style={{ fontSize: 11, color: T.textMuted, marginBottom: 12 }}>
                Usado em: {comp.usedIn.join(', ')}
              </p>
              <div style={{ padding: 16, background: T.bgOverlay, borderRadius: 10, border: `1px solid ${T.border}` }}>
                {comp.example()}
              </div>
            </ComponentSection>
          ))}
        </div>
      ))}
    </div>
  );
}


// ============================================================
// ABA 3 — PADROES
// ============================================================

function TabPadroes() {
  const [toastVisible, setToastVisible] = useState(false);
  const [toastType, setToastType] = useState<'success'|'error'|'warning'|'info'>('success');
  const [formError, setFormError] = useState('');
  const [btnLoading, setBtnLoading] = useState(false);

  const showToast = (type: 'success'|'error'|'warning'|'info') => {
    setToastType(type);
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), type === 'error' ? 5000 : 3000);
  };

  const toastColors = { success: T.success, error: T.danger, warning: T.warning, info: T.primary };
  const toastMessages = { success: 'Operação realizada com sucesso!', error: 'Erro ao processar. Tente novamente.', warning: 'Atenção: limite próximo do máximo.', info: 'Nova versão disponível.' };
  const toastIcons = { success: '✓', error: '✗', warning: '⚠', info: 'ℹ' };

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 16 }}>Padroes de Uso</h2>

      {/* Toast flutuante — usando componente Toast real */}
      {toastVisible && (
        <div style={{ position: 'fixed', top: 24, right: 24, zIndex: 9999 }}>
          <Toast message={toastMessages[toastType]} type={toastType} onClose={() => setToastVisible(false)} />
        </div>
      )}

      {/* Loading */}
      <div style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 8 }}>Loading</h3>
        <div style={{ color: T.textSecondary, fontSize: 13, lineHeight: 1.8 }}>
          <p>3 tipos permitidos:</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, margin: '12px 0' }}>
            <Card>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.success, marginBottom: 8 }}>Skeleton Shimmer</div>
              <div style={{ fontSize: 12, color: T.textSecondary }}>Usado para conteudo principal (tabelas, cards, listas). Componentes: PageShell, TableSkeleton, CardSkeleton, ListSkeleton.</div>
            </Card>
            <Card>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.success, marginBottom: 8 }}>Inline Spinner</div>
              <div style={{ fontSize: 12, color: T.textSecondary }}>Usado dentro de botoes ou acoes pontuais. Nunca como loading de pagina inteira.</div>
            </Card>
            <Card>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.success, marginBottom: 8 }}>Fade-in</div>
              <div style={{ fontSize: 12, color: T.textSecondary }}>Transicao opacity 0-1 apos loading. Ja incluso no PageShell automaticamente.</div>
            </Card>
          </div>
        </div>
      </div>

      {/* Animacoes */}
      <div style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 8 }}>Animacoes</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Card>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.success, marginBottom: 8 }}>FAZER</div>
            <ul style={{ fontSize: 12, color: T.textSecondary, paddingLeft: 16, lineHeight: 2 }}>
              <li>transition: 0.15s-0.3s ease</li>
              <li>opacity fade-in para conteudo</li>
              <li>hover: scale ou brightness sutil</li>
              <li>shimmer para skeletons</li>
            </ul>
          </Card>
          <Card>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.danger, marginBottom: 8 }}>NAO FAZER</div>
            <ul style={{ fontSize: 12, color: T.textSecondary, paddingLeft: 16, lineHeight: 2 }}>
              <li>Stagger animations em listas</li>
              <li>Slide-up / slide-down em itens</li>
              <li>Animacoes &gt; 0.3s</li>
              <li>Spring / bounce / elastic</li>
            </ul>
          </Card>
        </div>
      </div>

      {/* Layout & Responsividade */}
      <div style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 12 }}>Layout & Responsividade</h3>

        {/* Breakpoints */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.primary, marginBottom: 8 }}>Breakpoints</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
            {[
              { nome: 'xs', valor: '480px', desc: 'Celular pequeno' },
              { nome: 'sm', valor: '640px', desc: 'Celular grande' },
              { nome: 'md', valor: '768px', desc: 'Tablet (principal)' },
              { nome: 'lg', valor: '1024px', desc: 'Laptop' },
              { nome: 'xl', valor: '1400px', desc: 'Desktop' },
            ].map(bp => (
              <Card key={bp.nome}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.success, marginBottom: 4 }}>{bp.nome.toUpperCase()}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{bp.valor}</div>
                <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{bp.desc}</div>
              </Card>
            ))}
          </div>
        </div>

        {/* Grid Patterns */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.primary, marginBottom: 8 }}>Grid Patterns</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Card>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.text, marginBottom: 6 }}>4 Colunas — Metricas</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
                {[1,2,3,4].map(i => <div key={i} style={{ height: 24, background: T.primaryBg, borderRadius: 4 }} />)}
              </div>
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 6 }}>repeat(4, 1fr) · gap: 16px</div>
              <div style={{ fontSize: 10, color: T.textMuted }}>Mobile: 1fr · Tablet: 2 cols</div>
            </Card>
            <Card>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.text, marginBottom: 6 }}>3 Colunas — Cards</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
                {[1,2,3].map(i => <div key={i} style={{ height: 24, background: T.successBg, borderRadius: 4 }} />)}
              </div>
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 6 }}>repeat(3, 1fr) · gap: 16px</div>
              <div style={{ fontSize: 10, color: T.textMuted }}>Mobile: 1fr · Tablet: 2 cols</div>
            </Card>
            <Card>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.text, marginBottom: 6 }}>2 Colunas — Forms</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 4 }}>
                {[1,2].map(i => <div key={i} style={{ height: 24, background: T.purpleBg, borderRadius: 4 }} />)}
              </div>
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 6 }}>repeat(2, 1fr) · gap: 16px</div>
              <div style={{ fontSize: 10, color: T.textMuted }}>Mobile: 1fr</div>
            </Card>
          </div>
        </div>

        {/* Container Widths */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.primary, marginBottom: 8 }}>Container Widths</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[
              { nome: 'Admin', valor: '1400px', classe: '.container-admin', cor: T.primary },
              { nome: 'Landing', valor: '1200px', classe: '.container-landing', cor: T.success },
              { nome: 'Formulario', valor: '800px', classe: '.container-form', cor: T.purple },
              { nome: 'Modal', valor: '480px', classe: '.modal', cor: T.warning },
              { nome: 'Chat', valor: '100%', classe: 'full-width', cor: T.danger },
            ].map(c => (
              <div key={c.nome} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: c.cor, flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: T.text, width: 90 }}>{c.nome}</span>
                <span style={{ fontSize: 13, fontFamily: "'SF Mono', monospace", color: T.textSecondary }}>{c.valor}</span>
                <span style={{ fontSize: 11, color: T.textMuted }}>({c.classe})</span>
              </div>
            ))}
          </div>
        </div>

        {/* Classes CSS utilitarias */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.primary, marginBottom: 8 }}>Classes CSS Utilitarias</div>
          <div style={{ fontSize: 12, color: T.textSecondary, lineHeight: 2, fontFamily: "'SF Mono', monospace" }}>
            <div><span style={{ color: T.success }}>.grid-responsive-4</span> — 4 cols desktop, 2 tablet, 1 mobile</div>
            <div><span style={{ color: T.success }}>.grid-responsive-3</span> — 3 cols desktop, 2 tablet, 1 mobile</div>
            <div><span style={{ color: T.success }}>.grid-responsive-2</span> — 2 cols desktop, 1 mobile</div>
            <div><span style={{ color: T.success }}>.container-admin</span> — max-width 1400px centralizado</div>
            <div><span style={{ color: T.success }}>.container-form</span> — max-width 800px centralizado</div>
            <div><span style={{ color: T.success }}>.hide-mobile</span> / <span style={{ color: T.success }}>.show-mobile</span> — visibilidade por breakpoint</div>
            <div><span style={{ color: T.success }}>.table-responsive</span> — scroll horizontal em tabelas</div>
          </div>
        </div>

        {/* Link doc completo */}
        <div style={{ fontSize: 12, color: T.textMuted, borderTop: `1px solid ${T.border}`, paddingTop: 12 }}>
          📄 Documento completo: <a href="/admin/docs/layout-responsividade.md" target="_blank" style={{ color: T.primary }}>/admin/docs/layout-responsividade.md</a>
        </div>
      </div>

      {/* Cores de texto */}
      <div style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 8 }}>Cores de Texto</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 24, height: 24, borderRadius: 4, background: T.text }} />
            <span style={{ color: T.text, fontSize: 13 }}>#E8E8F0 — Texto principal (titulos, conteudo)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 24, height: 24, borderRadius: 4, background: T.textSecondary }} />
            <span style={{ color: T.textSecondary, fontSize: 13 }}>#9CA3AF — Labels, subtitulos, texto secundario</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 24, height: 24, borderRadius: 4, background: T.textMuted }} />
            <span style={{ color: T.textMuted, fontSize: 13 }}>#6B7280 — Texto muted, helpers, captions</span>
          </div>
        </div>
      </div>

      {/* Hierarquia de componentes */}
      <div style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 8 }}>Hierarquia de Componentes</h3>
        <div style={{ fontSize: 13, color: T.textSecondary, lineHeight: 2 }}>
          <div>- <strong style={{ color: T.text }}>PageShell</strong> — Container de pagina com loading automatico</div>
          <div>- <strong style={{ color: T.text }}>SectionHeader</strong> — Titulo + subtitulo + acao de cada secao</div>
          <div>- <strong style={{ color: T.text }}>TabBar</strong> — Navegacao entre abas</div>
          <div>- <strong style={{ color: T.text }}>Card / MetricCard</strong> — Container de conteudo</div>
          <div>- <strong style={{ color: T.text }}>FormGroup</strong> — Wrapper para campos de formulario</div>
          <div>- <strong style={{ color: T.text }}>Button / Badge</strong> — Acoes e status</div>
        </div>
      </div>

      {/* ============ INTERAÇÕES (FASE 3.4) ============ */}
      <Divider />
      <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 16, marginTop: 16 }}>Interações</h2>

      {/* Hover Effect Demo */}
      <div style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 12 }}>Hover Effects</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          {[
            { label: 'Border Highlight', hoverStyle: 'border' },
            { label: 'Background Lift', hoverStyle: 'bg' },
            { label: 'Scale Sutil', hoverStyle: 'scale' },
          ].map(item => (
            <div
              key={item.label}
              style={{
                padding: 20, borderRadius: 10, cursor: 'pointer',
                background: T.bgCard,
                border: `1px solid ${T.border}`,
                transition: 'all 0.15s ease',
                textAlign: 'center' as const,
              }}
              onMouseEnter={(e: any) => {
                if (item.hoverStyle === 'border') e.currentTarget.style.borderColor = T.borderHover;
                if (item.hoverStyle === 'bg') e.currentTarget.style.background = T.border;
                if (item.hoverStyle === 'scale') e.currentTarget.style.transform = 'scale(1.02)';
              }}
              onMouseLeave={(e: any) => {
                e.currentTarget.style.borderColor = T.border;
                e.currentTarget.style.background = T.bgCard;
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 4 }}>{item.label}</div>
              <div style={{ fontSize: 11, color: T.textMuted }}>Passe o mouse</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 11, color: T.textMuted, marginTop: 8 }}>Padrao: <code style={{ color: T.textSecondary }}>transition: all 0.15s ease</code> (token: transitionFast)</div>
      </div>

      {/* Button States Demo */}
      <div style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 12 }}>Estados de Botão</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button>Normal</Button>
          <Button disabled>Disabled</Button>
          <Button loading={btnLoading} onClick={() => { setBtnLoading(true); setTimeout(() => setBtnLoading(false), 2000); }}>
            {btnLoading ? 'Salvando...' : 'Clique → Loading'}
          </Button>
          <Button variant="danger">Danger</Button>
          <Button variant="ghost">Ghost</Button>
        </div>
        <div style={{ fontSize: 11, color: T.textMuted, marginTop: 8 }}>Active: <code style={{ color: T.textSecondary }}>scale(0.98)</code> · Disabled: <code style={{ color: T.textSecondary }}>opacity: 0.5</code> · Loading: spinner inline</div>
      </div>

      {/* Toast Demo */}
      <div style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 12 }}>Toast / Notificações</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="ghost" onClick={() => showToast('success')}>✓ Success</Button>
          <Button variant="ghost" onClick={() => showToast('error')}>✗ Error</Button>
          <Button variant="ghost" onClick={() => showToast('warning')}>⚠ Warning</Button>
          <Button variant="ghost" onClick={() => showToast('info')}>ℹ Info</Button>
        </div>
        <div style={{ fontSize: 11, color: T.textMuted, marginTop: 8 }}>Posição: top-right · Duração: 3s (info/success), 5s (error), 4s (warning)</div>
      </div>

      {/* Form Validation Demo */}
      <div style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 12 }}>Validação de Formulário</h3>
        <div style={{ maxWidth: 400 }}>
          <FormGroup label="Email" error={formError} helper="Validação ocorre ao sair do campo (onBlur)">
            <Input
              type="email"
              placeholder="usuario@email.com"
              error={!!formError}
              onBlur={(e: any) => {
                const v = e.target.value;
                if (v && !v.includes('@')) setFormError('Email inválido. Inclua um @ no endereço.');
                else setFormError('');
              }}
            />
          </FormGroup>
        </div>
      </div>

      {/* Transition Tokens */}
      <div style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 12 }}>Tokens de Transição</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          {[
            { nome: 'transitionFast', valor: '0.15s ease', uso: 'Hover, focus, micro-interações' },
            { nome: 'transitionNormal', valor: '0.2s ease', uso: 'Fade-in, modais, conteúdo' },
            { nome: 'transitionSlow', valor: '0.3s ease', uso: 'Sidebar, painéis grandes' },
          ].map(t => (
            <Card key={t.nome}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.primary, fontFamily: T.fontMono, marginBottom: 4 }}>{t.nome}</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 4 }}>{t.valor}</div>
              <div style={{ fontSize: 11, color: T.textMuted }}>{t.uso}</div>
            </Card>
          ))}
        </div>
      </div>

      {/* Link doc completo */}
      <div style={{ fontSize: 12, color: T.textMuted, borderTop: `1px solid ${T.border}`, paddingTop: 12 }}>
        📄 Documento completo: <a href="/admin/docs/padroes-interacao.md" target="_blank" style={{ color: T.primary }}>/admin/docs/padroes-interacao.md</a>
      </div>
    </div>
  );
}

// ============================================================
// ABA 4 — ESTRATEGIA (documentos de design)
// ============================================================

function TabEstrategia() {
  const docs = [
    {
      titulo: 'Analise de Concorrencia Visual',
      descricao: 'Pesquisa de 12 plataformas concorrentes (Vercel, Linear, Supabase, Railway, etc). Padroes visuais, navegacao, cores, tipografia.',
      arquivo: 'design-system-analise-concorrencia.md',
      tamanho: '22KB',
      fase: '1.1',
    },
    {
      titulo: 'Personas e Jornadas de Usuario',
      descricao: '5 personas detalhadas (Empreendedor Solo, Dev Tecnica, Gestor de Agencia, Early Adopter, Afiliado) + 3 jornadas + mapa de empatia.',
      arquivo: 'personas-jornadas.md',
      tamanho: '36KB',
      fase: '1.2',
    },
    {
      titulo: 'Auditoria Visual',
      descricao: 'Auditoria completa do estado visual atual do WhiteLabelClaw. Inconsistencias, gaps, oportunidades de melhoria.',
      arquivo: 'design-system-auditoria-visual.md',
      tamanho: '11KB',
      fase: '1.3',
    },
    {
      titulo: 'Brand Story',
      descricao: 'Historia da marca WhiteLabelClaw: origem, proposito, visao, diferencial, personalidade.',
      arquivo: 'brand-story.md',
      tamanho: '5KB',
      fase: '2.1',
    },
    {
      titulo: 'Tom de Voz',
      descricao: 'Guia de comunicacao: principios, personalidade, exemplos por contexto (UI, erro, marketing, docs).',
      arquivo: 'tom-de-voz.md',
      tamanho: '8.5KB',
      fase: '2.2',
    },
    {
      titulo: 'Posicionamento de Mercado',
      descricao: 'Analise competitiva, proposta de valor unica, matriz de posicionamento, estrategia de pricing.',
      arquivo: 'posicionamento-mercado.md',
      tamanho: '7.3KB',
      fase: '2.3',
    },
    {
      titulo: 'Design Tokens',
      descricao: 'Sistema centralizado de tokens: cores, tipografia, espacamentos, sombras. Base do Design System.',
      arquivo: 'design-tokens.md',
      tamanho: '~15KB',
      fase: '3.1',
    },
    {
      titulo: 'Catalogo de Componentes',
      descricao: '33 componentes reutilizaveis documentados: props, exemplos, categorias.',
      arquivo: 'catalogo-componentes.md',
      tamanho: '~25KB',
      fase: '3.2',
    },
    {
      titulo: 'Layout & Responsividade',
      descricao: 'Padroes de grid, breakpoints, containers e regras de responsividade.',
      arquivo: 'layout-responsividade.md',
      tamanho: '7KB',
      fase: '3.3',
    },
    {
      titulo: 'Padroes de Interacao',
      descricao: 'Loading states, animacoes, feedback visual, transicoes e microinteracoes.',
      arquivo: 'padroes-interacao.md',
      tamanho: '10KB',
      fase: '3.4',
    },
  ];

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 8 }}>Documentos de Estrategia</h2>
      <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 24, lineHeight: 1.6 }}>
        Documentacao completa do processo de design seguindo metodologia Big Tech: Pesquisa → Estrategia de Marca → Design System → Implementacao.
      </p>

      {/* Roadmap visual */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { fase: '1', label: 'Pesquisa', cor: T.success },
          { fase: '2', label: 'Estrategia', cor: T.primary },
          { fase: '3', label: 'Design System', cor: T.purple },
          { fase: '4', label: 'Implementacao', cor: T.warning },
        ].map(f => (
          <div key={f.fase} style={{
            padding: '6px 14px', borderRadius: 20,
            background: `${f.cor}15`, border: `1px solid ${f.cor}30`,
            fontSize: 12, fontWeight: 600, color: f.cor,
          }}>
            FASE {f.fase}: {f.label}
          </div>
        ))}
      </div>

      {/* Cards dos documentos */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {docs.map(doc => (
          <Card key={doc.arquivo}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{doc.titulo}</div>
              <Badge variant="info">FASE {doc.fase}</Badge>
            </div>
            <div style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.6, marginBottom: 12 }}>{doc.descricao}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: T.textMuted, fontFamily: T.fontMono }}>{doc.arquivo} ({doc.tamanho})</span>
              <a
                href={`/admin/docs/${doc.arquivo}`}
                target="_blank"
                rel="noopener"
                style={{ fontSize: 12, color: T.primary, textDecoration: 'none', fontWeight: 500 }}
              >
                Ver documento →
              </a>
            </div>
          </Card>
        ))}
      </div>

      {/* Resumo do progresso */}
      <div style={{ marginTop: 32, padding: 16, background: T.purpleBg, borderRadius: 10, border: `1px solid ${T.purpleBg}` }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.purple, marginBottom: 8 }}>Progresso do Design System</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: T.success, fontWeight: 600, marginBottom: 4 }}>Concluido</div>
            <div style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.8 }}>
              FASE 1.1 — Analise de Concorrencia ✅<br/>
              FASE 1.2 — Personas e Jornadas ✅<br/>
              FASE 1.3 — Auditoria Visual ✅<br/>
              FASE 2.1 — Brand Story ✅<br/>
              FASE 2.2 — Tom de Voz ✅<br/>
              FASE 2.3 — Posicionamento ✅<br/>
              FASE 3.1 — Design Tokens ✅<br/>
              FASE 3.2 — Biblioteca de Componentes (33) ✅<br/>
              FASE 3.3 — Layout e Responsividade ✅<br/>
              FASE 3.4 — Padroes de Interacao ✅<br/>
              FASE 4.1 — Migracao Frontend (107+ paginas) ✅<br/>
              FASE 4.2 — Redesign Login/LLM/Upgrade ✅
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: T.warning, fontWeight: 600, marginBottom: 4 }}>Pendente</div>
            <div style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.8 }}>
              FASE 4.3 — Docs & API Visual
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// ABA 5 — PADRÕES DE PÁGINA
// ============================================================

function PatternBlock({ title, description, usedIn, notes, children }: { title: string; description: string; usedIn: string; notes?: string; children: any }) {
  return (
    <div style={{ marginBottom: 28, paddingBottom: 20, borderBottom: `1px solid ${T.border}` }}>
      <h3 style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 4 }}>{title}</h3>
      <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 8 }}>{description}</p>
      <div style={{ marginBottom: 8 }}>{children}</div>
      <div style={{ fontSize: 11, color: T.textSecondary }}>
        <strong>Usado em:</strong> {usedIn}
        {notes && <span style={{ marginLeft: 12 }}>| <strong>Nota:</strong> {notes}</span>}
      </div>
    </div>
  );
}

function TabPaginas() {
  const amber = '#D4A053';
  const purpleTC = '#8B7EC8';
  const cyan = '#4DB8C7';
  const greenTC = '#5DAA8C';

  return (
    <div>
      <SectionHeader title="Padrões de Página" subtitle="Componentes visuais específicos de cada página — referência para manter consistência" />

      {/* ========== CHAT LEGACY ========== */}
      <h2 style={{ fontSize: 20, fontWeight: 700, color: T.text, marginTop: 32, marginBottom: 8 }}>🗨️ Chat Legacy <span style={{ fontSize: 13, fontWeight: 400, color: T.textSecondary }}>/chat-legacy</span></h2>
      <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 16 }}>Padrões exclusivos do Chat Legacy (Chat.tsx)</p>

      {/* Tool Call Card */}
      <PatternBlock title="🔧 Tool Call Card" description="Cards de ferramentas executadas pelo agente com ícones e cores por tipo" usedIn="/chat-legacy" notes="16 tipos de tool call com ícones e cores únicos">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 400 }}>
          {[
            { label: 'Terminal (exec)', color: amber, icon: '⬛', summary: 'npm run build' },
            { label: 'Editando (Edit)', color: purpleTC, icon: '✏️', summary: 'src/index.ts — 3 linhas' },
            { label: 'Pesquisa (web_search)', color: cyan, icon: '🔍', summary: '"deploy cloudflare workers"' },
            { label: 'Mensagem (message)', color: greenTC, icon: '💬', summary: 'Enviado para #geral' },
          ].map((t, i) => (
            <div key={i} style={{ background: `${t.color}14`, borderLeft: `3px solid ${t.color}`, padding: '8px 12px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14 }}>{t.icon}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: t.color }}>{t.label}</span>
              <span style={{ fontSize: 12, color: T.textSecondary, marginLeft: 'auto' }}>{t.summary}</span>
            </div>
          ))}
        </div>
      </PatternBlock>

      {/* Machine Selector */}
      <PatternBlock title="🖥️ Machine Selector" description="Dropdown de seleção de máquina com OS e status" usedIn="/chat-legacy">
        <div style={{ background: T.bgSecondary, borderRadius: 12, padding: 8, maxWidth: 280 }}>
          {[
            { os: '🍎', name: 'Elon Parker', sub: 'macOS', online: true },
            { os: '🐧', name: 'Servidor Prod', sub: 'Linux', online: false },
            { os: '🪟', name: 'Desktop', sub: 'Windows', online: true },
          ].map((m, i) => (
            <div key={i} style={{ padding: '10px 12px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: T.text }}>
              <span style={{ fontSize: 18 }}>{m.os}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{m.name}</div>
                <div style={{ fontSize: 11, color: T.textSecondary }}>{m.sub}</div>
              </div>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: m.online ? T.success : T.danger }} />
            </div>
          ))}
        </div>
      </PatternBlock>

      {/* Deploy Status Banner */}
      <PatternBlock title="🚀 Deploy Status Banner" description="Banner de status do último deploy com link para Actions" usedIn="/chat-legacy">
        <div style={{ background: `${T.success}18`, border: `1px solid ${T.success}40`, borderRadius: 10, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, maxWidth: 500 }}>
          <span style={{ fontSize: 14 }}>✅</span>
          <span style={{ fontSize: 13, color: T.text, flex: 1 }}>Deploy <strong>fc4bddc6</strong> — success — 2 min atrás</span>
          <span style={{ fontSize: 12, color: T.primary, cursor: 'pointer' }}>Ver Actions →</span>
        </div>
      </PatternBlock>

      {/* Attach Menu */}
      <PatternBlock title="📎 Attach Menu" description="Dropdown de anexo com opções de mídia e documento" usedIn="/chat-legacy">
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <div style={{ background: T.bgSecondary, borderRadius: 12, padding: 8, boxShadow: `0 8px 32px ${T.blackAlpha40}`, minWidth: 180 }}>
            {[
              { emoji: '📷', label: 'Enviar Imagem' },
              { emoji: '📄', label: 'Enviar Documento' },
            ].map((item, i) => (
              <div key={i} style={{ padding: '10px 14px', borderRadius: 8, fontSize: 13, color: T.text, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>{item.emoji}</span> {item.label}
              </div>
            ))}
          </div>
        </div>
      </PatternBlock>

      {/* Voice Button */}
      <PatternBlock title="🎙️ Voice Button" description="Botão de gravação de voz com estado normal e ativo" usedIn="/chat-legacy">
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: T.bgSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.textSecondary, fontSize: 18, cursor: 'pointer' }}>🎙️</div>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: `${T.danger}30`, display: `flex`, alignItems: 'center', justifyContent: 'center', color: T.danger, fontSize: 18, cursor: 'pointer', boxShadow: `0 0 12px ${T.danger}40` }}>🎙️</div>
          <span style={{ fontSize: 11, color: T.textSecondary }}>Normal → Gravando</span>
        </div>
      </PatternBlock>

      {/* Push Banner */}
      <PatternBlock title="🔔 Push Banner" description="Banner de ativação de notificações push" usedIn="/chat-legacy">
        <div style={{ background: `${T.warning}18`, border: `1px solid ${T.warning}40`, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, maxWidth: 500 }}>
          <span style={{ fontSize: 14, color: T.warning, flex: 1 }}>Ative notificações push para saber quando seu agente responde</span>
          <Button variant="ghost" style={{ background: T.warning, color: T.bg, border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Ativar</Button>
          <span style={{ color: T.textSecondary, cursor: 'pointer', fontSize: 16 }}>✕</span>
        </div>
      </PatternBlock>

      {/* IC Icons System */}
      <PatternBlock title="🎨 IC Icons System" description="Sistema de 16 ícones SVG para tool calls e status" usedIn="/chat-legacy" notes="16 ícones com cores únicas por tipo">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {[
            { icon: '⬛', label: 'exec', color: amber },
            { icon: '✏️', label: 'edit', color: purpleTC },
            { icon: '🔍', label: 'search', color: cyan },
            { icon: '💬', label: 'message', color: greenTC },
            { icon: '📄', label: 'read', color: T.primary },
            { icon: '✍️', label: 'write', color: T.warning },
            { icon: '🌐', label: 'browser', color: T.info || T.primary },
            { icon: '📸', label: 'image', color: T.purple },
          ].map((ic, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, background: `${ic.color}14`, padding: '4px 8px', borderRadius: 6, fontSize: 11, color: ic.color }}>
              <span>{ic.icon}</span> {ic.label}
            </div>
          ))}
        </div>
      </PatternBlock>

      {/* ========== CHAT HUB ========== */}
      <h2 style={{ fontSize: 20, fontWeight: 700, color: T.text, marginTop: 40, marginBottom: 8 }}>💬 Chat Hub <span style={{ fontSize: 13, fontWeight: 400, color: T.textSecondary }}>/chat</span></h2>
      <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 16 }}>Padrões exclusivos do Chat Hub (ChatHubPage.tsx)</p>

      {/* Hub 1. ConversationList */}
      <PatternBlock title="📋 ConversationList" description="Lista de conversas na sidebar com dot colorido, título truncado e subtítulo" usedIn="/chat">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 280 }}>
          {/* Ativo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, background: T.primaryBg, border: `1px solid ${T.primaryBgStrong}` }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.primary, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Deploy automático com CI/CD</div>
              <div style={{ fontSize: 11, color: T.textSecondary }}>5 msgs · 2 min atrás</div>
            </div>
          </div>
          {/* Hover */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, background: T.bgCard }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.success, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Refatorar componentes React</div>
              <div style={{ fontSize: 11, color: T.textSecondary }}>12 msgs · 1h atrás</div>
            </div>
          </div>
          {/* Normal */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.primary, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Análise de SEO do site</div>
              <div style={{ fontSize: 11, color: T.textSecondary }}>3 msgs · 5h atrás</div>
            </div>
          </div>
        </div>
      </PatternBlock>

      {/* Hub 2. AgentList */}
      <PatternBlock title="🤖 AgentList" description="Lista de agentes na sidebar com avatar, dot online/offline e subtipo" usedIn="/chat">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 260 }}>
          {/* LLM online */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8 }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: `linear-gradient(135deg, ${T.primary}, ${T.purple})`, display: `flex`, alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>⚡</div>
              <span style={{ position: 'absolute', bottom: -1, right: -1, width: 10, height: 10, borderRadius: '50%', background: T.success, border: `2px solid ${T.bg}` }} />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Claude Sonnet</div>
              <div style={{ fontSize: 10, color: T.textSecondary }}>LLM direto</div>
            </div>
          </div>
          {/* Agente offline */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8 }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: `linear-gradient(135deg, ${T.success}, #059669)`, display: `flex`, alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>🤖</div>
              <span style={{ position: 'absolute', bottom: -1, right: -1, width: 10, height: 10, borderRadius: '50%', background: T.textDisabled, border: `2px solid ${T.bg}` }} />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Elon Parker</div>
              <div style={{ fontSize: 10, color: T.textSecondary }}>Agente</div>
            </div>
          </div>
        </div>
      </PatternBlock>

      {/* Hub 3. ModelSelector */}
      <PatternBlock title="🔽 ModelSelector" description="Dropdown de seleção de modelo LLM com provider badge" usedIn="/chat">
        <div style={{ maxWidth: 280 }}>
          {/* Botão */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.bgSecondary, border: `1px solid ${T.border}`, borderRadius: 10, padding: '8px 14px', marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.text, flex: 1 }}>Claude Sonnet 4.5</span>
            <span style={{ fontSize: 10, color: T.textSecondary }}>▼</span>
          </div>
          {/* Dropdown aberto */}
          <div style={{ background: T.bgSecondary, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden' }}>
            {[
              { name: 'GPT-4o', provider: 'OpenAI', color: '#10A37F' },
              { name: 'Claude Sonnet', provider: 'Anthropic', color: '#D97757' },
              { name: 'Gemini Flash', provider: 'Google', color: T.primary },
            ].map((m, i) => (
              <div key={i} style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: i < 2 ? `1px solid ${T.border}` : 'none' }}>
                <span style={{ fontSize: 13, color: T.text }}>{m.name}</span>
                <span style={{ fontSize: 9, background: `${m.color}20`, color: m.color, padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>{m.provider}</span>
              </div>
            ))}
          </div>
        </div>
      </PatternBlock>

      {/* Hub 4. BalanceIndicator */}
      <PatternBlock title="💰 BalanceIndicator" description="Indicador de saldo com cor por nível (alto/médio/baixo)" usedIn="/chat">
        <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: T.success }}>$25.50</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: T.warning }}>$3.20</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: T.danger }}>$0.50</span>
          <span style={{ fontSize: 10, color: T.textSecondary }}>alto · médio · baixo</span>
        </div>
      </PatternBlock>

      {/* Hub 5. PromoBannerModal */}
      <PatternBlock title="🎉 PromoBannerModal" description="Modal promocional com gradient, CTA e countdown" usedIn="/chat">
        <div style={{ background: 'linear-gradient(135deg, #7C3AED, #2563EB)', borderRadius: 16, padding: 24, maxWidth: 360, position: 'relative' }}>
          <span style={{ position: 'absolute', top: 10, right: 14, color: T.textMuted, cursor: 'pointer', fontSize: 16 }}>✕</span>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.text, marginBottom: 6 }}>50% OFF primeiro mês!</div>
          <div style={{ fontSize: 13, color: T.text, marginBottom: 16 }}>Aproveite antes que acabe</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
            {['2d', '14h', '32m', '15s'].map((seg, i) => (
              <span key={i} style={{ background: T.borderHover, color: T.text, padding: '4px 8px', borderRadius: 4, fontFamily: 'monospace', fontSize: 13, fontWeight: 700 }}>{seg}</span>
            ))}
          </div>
          <Button variant="ghost" style={{ background: '#fff', color: T.purple, border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Garantir Desconto</Button>
        </div>
      </PatternBlock>

      {/* Hub 6. SlotsRemainingBadge */}
      <PatternBlock title="🏷️ SlotsRemainingBadge" description="Badge de urgência com 3 níveis de vagas restantes" usedIn="/chat">
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 700, background: T.bgActive, color: T.text }}>120 vagas</span>
          <span style={{ display: 'inline-flex', padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 700, background: T.warningBg, color: '#FEF3C7' }}>18 vagas</span>
          <span style={{ display: 'inline-flex', padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 700, background: T.dangerBg, color: '#FEE2E2' }}>3 vagas!</span>
        </div>
      </PatternBlock>

      {/* Hub 7. CountdownTimer */}
      <PatternBlock title="⏱️ CountdownTimer" description="Timer regressivo com segmentos mono-espaçados" usedIn="/chat">
        <div style={{ display: 'flex', gap: 6 }}>
          {['2d', '14h', '32m', '15s'].map((seg, i) => (
            <span key={i} style={{ background: T.bgActive, color: T.text, padding: '2px 6px', borderRadius: 4, fontFamily: 'monospace', fontSize: 14, fontWeight: 600 }}>{seg}</span>
          ))}
        </div>
      </PatternBlock>

      {/* Hub 8. RechargeModal */}
      <PatternBlock title="💳 RechargeModal" description="Modal de recarga de saldo com grid de valores e método de pagamento" usedIn="/chat">
        <div style={{ background: T.bgSecondary, borderRadius: 16, padding: 20, maxWidth: 320, border: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 16 }}>Recarregar Saldo</div>
          {/* Grid valores */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
            {[5, 10, 25, 50].map(v => (
              <div key={v} style={{ padding: '12px 0', borderRadius: 10, textAlign: 'center', fontSize: 16, fontWeight: 700, color: v === 10 ? T.primary : T.text, background: v === 10 ? `${T.primary}15` : T.bgCard, border: v === 10 ? `2px solid ${T.primary}` : '2px solid transparent', cursor: 'pointer' }}>${v}</div>
            ))}
          </div>
          {/* Tabs método */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <span style={{ flex: 1, textAlign: 'center', padding: '6px 0', borderRadius: 8, fontSize: 12, fontWeight: 600, background: `${T.primary}15`, color: T.primary }}>Pix</span>
            <span style={{ flex: 1, textAlign: 'center', padding: '6px 0', borderRadius: 8, fontSize: 12, fontWeight: 600, background: T.bgCard, color: T.textSecondary }}>Cartão</span>
          </div>
          <Button variant="ghost" style={{ width: '100%', background: T.primary, color: T.text, border: 'none', borderRadius: 10, padding: '10px 0', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Recarregar $10</Button>
        </div>
      </PatternBlock>

      {/* Hub 9. StreamingCursor */}
      <PatternBlock title="▌ StreamingCursor" description="Cursor piscante inline durante streaming de resposta" usedIn="/chat">
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 14, color: T.text }}>Processando resposta</span>
          <span style={{ display: 'inline-block', width: 2, height: 16, background: T.primary, borderRadius: 1 }} />
        </div>
      </PatternBlock>

      {/* Hub 10. TokenCounter */}
      <PatternBlock title="🔢 TokenCounter" description="Badge com contagem de tokens enviados/recebidos por mensagem" usedIn="/chat">
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: T.textSecondary, opacity: 0.6, fontFamily: 'monospace' }}>↑ 245 ↓ 1.2k</span>
          <span style={{ fontSize: 10, color: T.textSecondary, opacity: 0.6, fontFamily: 'monospace' }}>↑ 89 ↓ 3.4k</span>
          <span style={{ fontSize: 10, color: T.textSecondary, opacity: 0.6, fontFamily: 'monospace' }}>↑ 512 ↓ 890</span>
        </div>
      </PatternBlock>

      {/* Hub 11. ChatHeader */}
      <PatternBlock title="📌 ChatHeader" description="Header do chat ativo com nome, status, modelo e saldo" usedIn="/chat">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: `1px solid ${T.border}` }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Elon Parker</span>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.success }} />
          <span style={{ fontSize: 14, color: T.textSecondary }}>· Claude Sonnet 4.5</span>
          <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 600, color: T.success }}>$12.50</span>
        </div>
      </PatternBlock>

      {/* Hub 12. EmptyConversation */}
      <PatternBlock title="💭 EmptyConversation" description="Tela vazia quando nenhuma conversa está selecionada" usedIn="/chat">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: 20, background: T.bgCard, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, marginBottom: 16 }}>💬</div>
          <div style={{ fontSize: 16, color: T.textSecondary, fontWeight: 600, marginBottom: 6 }}>Selecione um agente para começar</div>
          <div style={{ fontSize: 13, color: T.textSecondary, opacity: 0.6 }}>Escolha um agente na sidebar ou crie uma nova conversa</div>
        </div>
      </PatternBlock>

      {/* Hub 13. COLORS Object */}
      <PatternBlock title="🎨 COLORS Object (Referência)" description="Mapeamento de cores do Chat Hub para tokens do Design System" usedIn="/chat">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, maxWidth: 400 }}>
          {[
            { key: 'bgPage', val: 'T.bg', color: T.bg },
            { key: 'bgSidebar', val: 'T.bg', color: T.bg },
            { key: 'bgChat', val: 'T.bg', color: T.bg },
            { key: 'userBubble', val: 'gradient azul/roxo', color: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' },
            { key: 'assistantBubble', val: T.bgCard, color: T.bgCard },
            { key: 'textPrimary', val: 'T.text', color: T.text },
            { key: 'textMuted', val: 'T.textSecondary', color: T.textSecondary },
            { key: 'accentPrimary', val: 'T.primary', color: T.primary },
            { key: 'accentGreen', val: 'T.success', color: T.success },
            { key: 'border', val: T.bgHover, color: T.bgHover },
          ].map((c, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', borderRadius: 6, background: T.bgSubtle }}>
              <span style={{ width: 14, height: 14, borderRadius: 3, background: c.color, border: `1px solid ${T.bgActive}`, flexShrink: 0 }} />
              <span style={{ fontSize: 11, fontFamily: 'monospace', color: T.textSecondary }}>{c.key}</span>
              <span style={{ fontSize: 10, color: T.primary, marginLeft: 'auto' }}>{c.val}</span>
            </div>
          ))}
        </div>
      </PatternBlock>

      {/* ========== COMPARTILHADOS ========== */}
      <h2 style={{ fontSize: 20, fontWeight: 700, color: T.text, marginTop: 40, marginBottom: 8 }}>🔄 Compartilhados</h2>
      <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 16 }}>Padrões usados em ambos os chats (Legacy + Hub)</p>

      {/* Chat Bubble */}
      <PatternBlock title="💬 Chat Bubble (Balão de Mensagem)" description="Balões de conversa com estilos distintos para usuário e assistente" usedIn="/chat-legacy, /chat">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 500 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div>
              <div style={{ background: T.bgSecondary, color: T.text, padding: '12px 16px', borderRadius: '16px 16px 16px 4px', maxWidth: '80%', fontSize: 14 }}>
                Olá! Como posso ajudar?
              </div>
              <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 2 }}>10:30</div>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div>
              <div style={{ background: T.primary, color: T.text, padding: '12px 16px', borderRadius: '16px 16px 4px 16px', maxWidth: '80%', fontSize: 14 }}>
                Preciso de ajuda com deploy
              </div>
              <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 2, textAlign: 'right' }}>10:31</div>
            </div>
          </div>
        </div>
      </PatternBlock>

      {/* Code Block */}
      <PatternBlock title="📝 Code Block" description="Bloco de código com syntax highlighting e header com linguagem" usedIn="/chat-legacy, /chat">
        <div style={{ background: T.bgSecondary, borderRadius: 8, borderLeft: `3px solid ${T.primary}`, overflow: 'hidden', maxWidth: 450 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', borderBottom: `1px solid ${T.border}` }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: T.textSecondary }}>bash</span>
            <span style={{ fontSize: 11, color: T.textSecondary, cursor: 'pointer' }}>📋 Copiar</span>
          </div>
          <pre style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: T.text, padding: '6px 12px', margin: 0 }}>
            <span style={{ color: T.primary }}>$</span> npm run build <span style={{ color: T.danger }}>&amp;&amp;</span> npm start
          </pre>
        </div>
      </PatternBlock>

      {/* Inline Code */}
      <PatternBlock title="🔤 Inline Code" description="Código inline em meio ao texto" usedIn="/chat-legacy, /chat">
        <p style={{ fontSize: 14, color: T.text }}>
          Use o comando{' '}
          <code style={{ background: T.bgSecondary, color: T.purple, padding: '2px 7px', borderRadius: 5, fontSize: 12.5, fontFamily: "'JetBrains Mono', monospace" }}>
            npm install
          </code>
          {' '}para instalar dependências.
        </p>
      </PatternBlock>

      {/* Typing Indicator */}
      <PatternBlock title="⏳ Typing Indicator" description="Indicador de digitação com 3 pontos pulsantes" usedIn="/chat-legacy, /chat">
        <div style={{ display: 'flex', gap: 4, padding: '8px 16px', background: T.bgSecondary, borderRadius: 16, width: 'fit-content' }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: T.textSecondary, opacity: 0.4 + i * 0.3 }} />
          ))}
        </div>
      </PatternBlock>

      {/* Online/Offline Badge */}
      <PatternBlock title="🟢 Online/Offline Badge" description="Indicador de status com bolinha colorida" usedIn="/chat-legacy, /chat, /agentes">
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: T.success, display: 'inline-block' }} />
            <span style={{ fontSize: 13, color: T.text }}>Online</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: T.danger, display: 'inline-block' }} />
            <span style={{ fontSize: 13, color: T.text }}>Offline</span>
          </div>
        </div>
      </PatternBlock>

      {/* File Card / File Path */}
      <PatternBlock title="📁 File Card / File Path" description="Cards de arquivo com ícones coloridos por extensão + path clicável" usedIn="/chat-legacy, /chat">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {[
              { ext: '.docx', color: '#2B579A', emoji: '📘' },
              { ext: '.xlsx', color: '#217346', emoji: '📗' },
              { ext: '.js', color: '#F7DF1E', emoji: '🟨' },
              { ext: '.py', color: '#3776AB', emoji: '🐍' },
              { ext: '.pdf', color: T.danger, emoji: '📄' },
            ].map((f, i) => (
              <div key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: T.bgCard, borderRadius: 8, padding: '8px 12px' }}>
                <span style={{ fontSize: 16 }}>{f.emoji}</span>
                <span style={{ fontSize: 13, color: T.text }}>arquivo{f.ext}</span>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: f.color, display: 'inline-block' }} />
              </div>
            ))}
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: T.bgCard, borderRadius: 8, padding: '8px 12px', cursor: 'pointer', width: 'fit-content' }}>
            <span>📄</span>
            <span style={{ fontSize: 13, color: T.primary, fontFamily: "'JetBrains Mono', monospace" }}>apps/web/src/pages/Chat.tsx</span>
          </div>
        </div>
      </PatternBlock>

      {/* Image Modal */}
      <PatternBlock title="🖼️ Image Modal" description="Overlay escuro com imagem centralizada, click para fechar" usedIn="/chat-legacy, /chat">
        <div style={{ background: T.bgOverlay, borderRadius: 12, padding: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120, position: 'relative' }}>
          <div style={{ background: T.bgActive, borderRadius: 8, width: 120, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.textSecondary, fontSize: 12 }}>
            📷 Imagem
          </div>
          <span style={{ position: 'absolute', top: 8, right: 12, color: T.textSecondary, cursor: 'pointer', fontSize: 18 }}>✕</span>
        </div>
      </PatternBlock>

      {/* ========== INTEGRAÇÕES ========== */}
      <h2 style={{ fontSize: 20, fontWeight: 700, color: T.text, marginTop: 40, marginBottom: 16 }}>📋 Integrações</h2>

      {/* 15. Integration Card */}
      <PatternBlock title="🔗 Integration Card" description="Card de integração com status, badge de tipo e descrição" usedIn="/integrations">
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {[
            { name: 'Cloudflare', desc: 'CDN, DNS, Workers', badge: 'APIKEY', status: 'Conectado', statusColor: T.success },
            { name: 'GitHub', desc: 'Repositórios, CI/CD', badge: 'OAUTH', status: 'Disponível', statusColor: T.textSecondary },
          ].map((c, i) => (
            <div key={i} style={{ background: T.bgSecondary, borderRadius: 12, padding: 16, minWidth: 220, border: `1px solid ${T.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: T.border, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>☁️</div>
                <span style={{ fontWeight: 600, fontSize: 14, color: T.text }}>{c.name}</span>
              </div>
              <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 8 }}>{c.desc}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ background: `${T.purple}20`, color: T.purple, fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>{c.badge}</span>
                <span style={{ fontSize: 11, color: c.statusColor }}>● {c.status}</span>
              </div>
            </div>
          ))}
        </div>
      </PatternBlock>

      {/* 16. Connection Detail */}
      <PatternBlock title="🔐 Connection Detail" description="Accordion com campos mascarados de credenciais e ações" usedIn="/integrations">
        <div style={{ background: T.bgSecondary, borderRadius: 10, padding: 16, maxWidth: 400 }}>
          {[
            { label: 'API Token', value: '****abc123' },
            { label: 'Account ID', value: '****xyz789' },
          ].map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${T.bgCard}` }}>
              <span style={{ fontSize: 12, color: T.textSecondary }}>{f.label}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <code style={{ fontSize: 12, color: T.text, fontFamily: "'JetBrains Mono', monospace" }}>{f.value}</code>
                <span style={{ fontSize: 12, cursor: 'pointer', color: T.textSecondary }}>📋</span>
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Button variant="ghost" style={{ background: T.primary, color: T.text, border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer' }}>Salvar</Button>
            <Button variant="ghost" style={{ background: T.border, color: T.textSecondary, border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer' }}>Testar Conexão</Button>
          </div>
        </div>
      </PatternBlock>

      {/* ========== AGENTES ========== */}
      <h2 style={{ fontSize: 20, fontWeight: 700, color: T.text, marginTop: 40, marginBottom: 16 }}>🤖 Agentes</h2>

      {/* 17. Agent Card */}
      <PatternBlock title="🤖 Agent Card" description="Card de agente com avatar, modelo, status e canais" usedIn="/agentes">
        <div style={{ background: T.bgSecondary, borderRadius: 12, padding: 16, maxWidth: 320 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <Avatar name="Elon Parker" size={40} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: T.text }}>Elon Parker</div>
              <div style={{ fontSize: 11, color: T.textSecondary }}>Claude Sonnet</div>
            </div>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.success, marginLeft: 'auto' }} />
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            <span style={{ background: '#0088cc20', color: '#0088cc', fontSize: 11, padding: '3px 8px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 4 }}>📱 Telegram</span>
            <span style={{ background: '#5865F220', color: '#5865F2', fontSize: 11, padding: '3px 8px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 4 }}>💬 Discord</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <ProgressBar value={45} max={100} label="CPU 45%" />
            <ProgressBar value={62} max={100} label="RAM 62%" />
            <ProgressBar value={30} max={100} label="HD 30%" />
          </div>
        </div>
      </PatternBlock>

      {/* 18. Resource Bars */}
      <PatternBlock title="📊 Resource Bars" description="Barras de progresso empilhadas para métricas de recursos" usedIn="/agentes">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 300 }}>
          <ProgressBar value={45} max={100} label="CPU 45%" />
          <ProgressBar value={62} max={100} label="RAM 62%" />
          <ProgressBar value={30} max={100} label="HD 30%" />
        </div>
      </PatternBlock>

      {/* 19. Channel Badge */}
      <PatternBlock title="📡 Channel Badge" description="Badges coloridos por canal de comunicação" usedIn="/agentes">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            { emoji: '📱', name: 'Telegram', color: '#0088cc' },
            { emoji: '💬', name: 'Discord', color: '#5865F2' },
            { emoji: '💚', name: 'WhatsApp', color: '#25D366' },
            { emoji: '🐦', name: 'Twitter', color: '#1DA1F2' },
          ].map((ch, i) => (
            <span key={i} style={{ background: `${ch.color}20`, color: ch.color, fontSize: 11, padding: '4px 8px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {ch.emoji} {ch.name}
            </span>
          ))}
        </div>
      </PatternBlock>

      {/* ========== INDIQUE & GANHE ========== */}
      <h2 style={{ fontSize: 20, fontWeight: 700, color: T.text, marginTop: 40, marginBottom: 16 }}>🎁 Indique & Ganhe</h2>

      {/* 20. Level Progress */}
      <PatternBlock title="📈 Level Progress" description="Barra de progresso com nível atual e próximo" usedIn="/referral">
        <div style={{ maxWidth: 350 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.textSecondary, marginBottom: 4 }}>
            <span>Starter (atual)</span>
            <span>Pro (próximo)</span>
          </div>
          <ProgressBar value={3} max={5} label="3/5 indicações (60%)" />
        </div>
      </PatternBlock>

      {/* 21. Share Button */}
      <PatternBlock title="🔗 Share Button" description="Botões de compartilhamento por canal" usedIn="/referral">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            { label: 'WhatsApp', color: '#25D366', emoji: '💚' },
            { label: 'Twitter', color: '#1DA1F2', emoji: '🐦' },
            { label: 'Copiar Link', color: T.textSecondary, emoji: '🔗' },
            { label: 'Email', color: T.purple, emoji: '✉️' },
          ].map((b, i) => (
            <Button variant="ghost" key={i} style={{ background: `${b.color}20`, color: b.color, border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {b.emoji} {b.label}
            </Button>
          ))}
        </div>
      </PatternBlock>

      {/* 22. Ranking Card */}
      <PatternBlock title="🏆 Ranking Card" description="Lista de ranking com destaque para o 1° lugar" usedIn="/referral">
        <div style={{ maxWidth: 350 }}>
          {[
            { pos: '🥇 1°', name: 'João Silva', count: 42, highlight: true },
            { pos: '🥈 2°', name: 'Maria Santos', count: 38, highlight: false },
            { pos: '🥉 3°', name: 'Pedro Lima', count: 25, highlight: false },
          ].map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', borderRadius: 8, marginBottom: 4, background: r.highlight ? `${T.warning}12` : `transparent` }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: T.text, width: 50 }}>{r.pos}</span>
              <span style={{ fontSize: 13, color: T.text, flex: 1 }}>{r.name}</span>
              <span style={{ fontSize: 12, color: T.textSecondary }}>{r.count} indicações</span>
            </div>
          ))}
        </div>
      </PatternBlock>
    </div>
  );
}

// ============================================================
// PAGINA PRINCIPAL
// ============================================================
// ABA 6 — TEMAS
// ============================================================

function ThemePreviewCard({ id, name, colors, isActive, onSelect }: {
  id: string; name: string; colors: any; isActive: boolean; onSelect: () => void;
}) {
  return (
    <div
      onClick={onSelect}
      style={{
        cursor: 'pointer',
        borderRadius: 12,
        border: isActive ? `2px solid ${T.primary}` : `1px solid ${T.border}`,
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        background: colors.bg,
        position: 'relative',
      }}
    >
      {/* Mini preview */}
      <div style={{ padding: 12, minHeight: 120 }}>
        {/* Header bar mock */}
        <div style={{
          display: 'flex', gap: 6, marginBottom: 10,
        }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: colors.danger }} />
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: colors.warning }} />
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: colors.success }} />
        </div>

        {/* Sidebar + content mock */}
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{
            width: 40, borderRadius: 4, background: colors.bgCard || colors.bgSecondary,
            padding: 6, display: 'flex', flexDirection: 'column', gap: 4,
          }}>
            <div style={{ height: 4, borderRadius: 2, background: colors.primary, width: '100%' }} />
            <div style={{ height: 4, borderRadius: 2, background: colors.textMuted, width: '70%', opacity: 0.5 }} />
            <div style={{ height: 4, borderRadius: 2, background: colors.textMuted, width: '85%', opacity: 0.5 }} />
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ height: 8, borderRadius: 3, background: colors.text, width: '60%', opacity: 0.8 }} />
            <div style={{ height: 20, borderRadius: 4, background: colors.primary, width: '40%' }} />
            <div style={{
              height: 24, borderRadius: colors.radiusMd || 6,
              border: `1px solid ${colors.border}`, background: colors.bgCard || 'transparent',
            }} />
            <div style={{ display: 'flex', gap: 4 }}>
              <div style={{ height: 6, borderRadius: 3, background: colors.success, width: '30%' }} />
              <div style={{ height: 6, borderRadius: 3, background: colors.accent || colors.purple, width: '25%' }} />
              <div style={{ height: 6, borderRadius: 3, background: colors.warning, width: '20%' }} />
            </div>
          </div>
        </div>
      </div>

      {/* Label */}
      <div style={{
        padding: '8px 12px',
        borderTop: `1px solid ${colors.border}`,
        background: colors.bgCard || T.bgOverlay,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>{name}</span>
        {isActive && (
          <span style={{
            fontSize: 10, padding: '2px 8px', borderRadius: 99,
            background: T.primary, color: T.text, fontWeight: 600,
          }}>ATIVO</span>
        )}
      </div>
    </div>
  );
}

function TabTemas() {
  const { themeId, setTheme, allThemes } = useTheme();

  return (
    <div>
      <h2 style={{ fontSize: T.textXl, fontWeight: T.weightBold, color: T.text, marginBottom: 4 }}>
        🎨 Temas
      </h2>
      <p style={{ color: T.textSecondary, fontSize: T.textSm, marginBottom: T.space6 }}>
        Clique em um tema para aplicar instantaneamente. {allThemes.length} temas disponíveis.
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: 16,
      }}>
        {allThemes.map((t) => (
          <ThemePreviewCard
            key={t.id}
            id={t.id}
            name={t.name}
            colors={t.colors}
            isActive={t.id === themeId}
            onSelect={() => setTheme(t.id)}
          />
        ))}
      </div>

      <div style={{ marginTop: 24, padding: 16, background: T.bgOverlay, borderRadius: 8, border: `1px solid ${T.border}` }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 8 }}>💡 Como funciona</h3>
        <ul style={{ color: T.textSecondary, fontSize: 13, lineHeight: 1.8, margin: 0, paddingLeft: 20 }}>
          <li>Temas trocam <strong>todos</strong> os tokens de cor (T.*) instantaneamente</li>
          <li>53 componentes ui/ atualizam automaticamente</li>
          <li>Tema salvo no localStorage — persiste entre sessões</li>
          <li>18 temas importados do <a href="https://tweakcn.com" target="_blank" style={{ color: T.primary }}>tweakcn.com</a> + WhiteLabelClaw v1</li>
        </ul>
      </div>
    </div>
  );
}

// ============================================================

export function AdminDesignSystem() {
  const [tab, setTab] = useState('identidade');

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: '24px 32px' }}>
      <SectionHeader title="Design System" subtitle="Referencia visual de componentes, cores e padroes" />

      <TabBar
        tabs={[
          { id: 'identidade', label: 'Identidade Visual' },
          { id: 'componentes', label: 'Componentes' },
          { id: 'padroes', label: 'Padroes' },
          { id: 'estrategia', label: 'Estrategia' },
          { id: 'paginas', label: '📄 Páginas' },
          { id: 'temas', label: '🎨 Temas' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'identidade' && <TabIdentidade />}
      {tab === 'componentes' && <TabComponentes />}
      {tab === 'padroes' && <TabPadroes />}
      {tab === 'estrategia' && <TabEstrategia />}
      {tab === 'paginas' && <TabPaginas />}
      {tab === 'temas' && <TabTemas />}
    </div>
  );
}
